'use client';

import type { StorefrontPopup } from '@eliadmin/core/src/api/popups/popup-types';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { deferOverlayComponent } from '../ui/dialog';
import type * as Runtime from './popup-runtime';
import React from 'react';
import { create } from 'zustand';

const PopupRuntime = deferOverlayComponent<typeof Runtime.PopupRuntime>(
  () => import('./popup-runtime').then(runtime => runtime.PopupRuntime),
  'PopupRuntime',
);

const POPUP_SUPPRESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const LEGACY_SUPPRESS_STORAGE_KEY = 'popup_suppress_v1';

type PopupQueueState = {
  contextualPopups: StorefrontPopup[];
  contextualSource: string | null;
  seenPopupKeys: string[];
  registerContextualPopups: (source: string, popups: StorefrontPopup[]) => void;
  unregisterContextualPopups: (source: string) => void;
  markPopupSeen: (key: string) => void;
};

const usePopupQueueStore = create<PopupQueueState>(set => ({
  contextualPopups: [],
  contextualSource: null,
  seenPopupKeys: [],
  registerContextualPopups: (source, popups) =>
    set({
      contextualSource: source,
      contextualPopups: popups,
    }),
  unregisterContextualPopups: source =>
    set(state =>
      state.contextualSource === source ? { contextualSource: null, contextualPopups: [] } : state,
    ),
  markPopupSeen: key =>
    set(state =>
      state.seenPopupKeys.includes(key) ? state : { seenPopupKeys: [...state.seenPopupKeys, key] },
    ),
}));

function popupSignature(popup: StorefrontPopup): string {
  return JSON.stringify({
    headline: popup.headline ?? '',
    content: popup.content ?? '',
    image: popup.image?.url ?? '',
    redirectUrl: popup.popup_redirect_url ?? '',
    buttonText: popup.popup_button_text ?? '',
  });
}

function legacyPopupSignature(popup: StorefrontPopup): string {
  return JSON.stringify({
    h: popup.headline ?? '',
    c: popup.content ?? '',
    i: popup.image?.url ?? '',
  });
}

function hashString(value: string): string {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
}

function popupIdentity(popup: StorefrontPopup): string {
  return popup.id ? `id-${popup.id}` : `legacy-${hashString(popupSignature(popup))}`;
}

function popupSeenKey(popup: StorefrontPopup): string {
  return `${popupIdentity(popup)}:${hashString(popupSignature(popup))}`;
}

function popupSuppressStorageKey(popup: StorefrontPopup): string {
  return `eliadmin:popup:suppress:${popupIdentity(popup)}`;
}

function isPopupSuppressed(popup: StorefrontPopup): boolean {
  try {
    const raw = localStorage.getItem(popupSuppressStorageKey(popup));
    if (raw) {
      const parsed = JSON.parse(raw) as { signature?: string; until?: number };
      if (
        parsed.signature === popupSignature(popup) &&
        typeof parsed.until === 'number' &&
        parsed.until > Date.now()
      ) {
        return true;
      }
    }

    const legacyRaw = localStorage.getItem(LEGACY_SUPPRESS_STORAGE_KEY);
    if (!legacyRaw) return false;

    const legacy = JSON.parse(legacyRaw) as { signature?: string; until?: number };
    return (
      legacy.signature === legacyPopupSignature(popup) &&
      typeof legacy.until === 'number' &&
      legacy.until > Date.now()
    );
  } catch {
    return false;
  }
}

function setPopupSuppressed(popup: StorefrontPopup, suppressed: boolean): void {
  try {
    const storageKey = popupSuppressStorageKey(popup);

    if (!suppressed) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        signature: popupSignature(popup),
        until: Date.now() + POPUP_SUPPRESS_TTL_MS,
      }),
    );
  } catch {
    // localStorage may be unavailable or full; popup rendering must continue.
  }
}

function uniqueActivePopups(popups: Array<StorefrontPopup | null | undefined>): StorefrontPopup[] {
  const seen = new Set<string>();

  return popups.filter((popup): popup is StorefrontPopup => {
    if (!popup?.is_active) return false;

    const key = popupSeenKey(popup);
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

export function ContextualPopups({ popups }: { popups?: StorefrontPopup[] | null }) {
  const source = React.useId();
  const registerContextualPopups = usePopupQueueStore(state => state.registerContextualPopups);
  const unregisterContextualPopups = usePopupQueueStore(state => state.unregisterContextualPopups);
  const normalizedPopups = React.useMemo(() => (Array.isArray(popups) ? popups : []), [popups]);

  React.useEffect(() => {
    registerContextualPopups(source, normalizedPopups);

    return () => unregisterContextualPopups(source);
  }, [normalizedPopups, registerContextualPopups, source, unregisterContextualPopups]);

  return null;
}

export function Popup() {
  const globalPopup = useGlobalStore(state => state.generalLayout?.popup);
  const contextualPopups = usePopupQueueStore(state => state.contextualPopups);
  const seenPopupKeys = usePopupQueueStore(state => state.seenPopupKeys);
  const markPopupSeen = usePopupQueueStore(state => state.markPopupSeen);

  const popups = React.useMemo(
    () => uniqueActivePopups([...contextualPopups, globalPopup]),
    [contextualPopups, globalPopup],
  );
  const queueKey = popups.map(popupSeenKey).join('|');
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [dontShowNext, setDontShowNext] = React.useState(false);
  const currentPopup = popups[currentIndex];
  const currentPopupKey = currentPopup ? popupSeenKey(currentPopup) : null;

  React.useEffect(() => {
    setCurrentIndex(0);
    setOpen(false);
  }, [queueKey]);

  React.useEffect(() => {
    if (!currentPopup || !currentPopupKey) return;

    setOpen(false);
    setDontShowNext(false);

    if (seenPopupKeys.includes(currentPopupKey) || isPopupSuppressed(currentPopup)) {
      const skipHandle = window.setTimeout(() => setCurrentIndex(index => index + 1), 0);
      return () => window.clearTimeout(skipHandle);
    }

    const seconds = Number(currentPopup.time ?? 0);
    if (!Number.isFinite(seconds) || seconds < 0) return;

    const openHandle = window.setTimeout(() => setOpen(true), seconds * 1000);
    return () => window.clearTimeout(openHandle);
  }, [currentPopup, currentPopupKey, queueKey, seenPopupKeys]);

  if (!currentPopup || !currentPopupKey) return null;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen && open) {
      markPopupSeen(currentPopupKey);
    }
  };

  const suppressCheckboxId = `popup-suppress-${popupIdentity(currentPopup)}`;

  return (
    <PopupRuntime
      currentPopup={currentPopup}
      currentIndex={currentIndex}
      popupCount={popups.length}
      open={open}
      handleOpenChange={handleOpenChange}
      dontShowNext={dontShowNext}
      onDontShowNextChange={checked => {
        setDontShowNext(checked);
        setPopupSuppressed(currentPopup, checked);
      }}
      suppressCheckboxId={suppressCheckboxId}
    />
  );
}
